import React from "react";
import { Receipt, Lock, ShieldCheck } from "lucide-react";
import { TASK } from "@/constants/testIds";

/** Pre-payment invoice preview + post-payment receipt with the single bundled transaction id. */
export default function InvoiceCard({ task, transactions = [] }) {
  const serviceFee = Number(task.service_fee_ngn || 0);
  const itemCost = Number(task.item_cost_ngn || task.budget_ngn || 0);
  const total = serviceFee + itemCost;
  const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;

  // Find the bundled / unified transaction for this task (most recent)
  const bundleTx = transactions.find((t) => t.kind === "bundle")
    || transactions.find((t) => t.kind === "service_fee" || t.kind === "item_cost");
  const txId = task.bundle_tx_id || bundleTx?.tx_id;

  const sfPaid = task.service_fee_status === "held_escrow" || task.service_fee_status === "released";
  const itemPaid = task.item_cost_status === "held_escrow" || task.item_cost_status === "released";

  return (
    <div className="eb-card p-6" data-testid={TASK.invoiceCard}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-[var(--eb-primary)]" />
          <h3 className="text-lg font-[800]">{itemCost > 0 ? "Invoice" : "Invoice (service only)"}</h3>
        </div>
        {txId && (
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--eb-text-dim)]">
            TX <span className="text-[var(--eb-text)] font-[700]">{txId.slice(-10)}</span>
          </div>
        )}
      </div>

      <table className="w-full mt-4 text-sm">
        <tbody>
          <tr className="border-b border-[var(--eb-border)]">
            <td className="py-2.5 text-[var(--eb-text-dim)]">Agent service fee</td>
            <td className="py-2.5 text-right font-[600]">{fmt(serviceFee)}</td>
            <td className="py-2.5 text-right pl-3">
              <span className={`text-[10px] uppercase tracking-[0.15em] ${sfPaid ? "text-[var(--eb-success)]" : "text-[var(--eb-text-faint)]"}`}>
                {sfPaid ? "in escrow" : "unfunded"}
              </span>
            </td>
          </tr>
          {itemCost > 0 && (
            <tr className="border-b border-[var(--eb-border)]">
              <td className="py-2.5 text-[var(--eb-text-dim)]">Item / purchase</td>
              <td className="py-2.5 text-right font-[600]">{fmt(itemCost)}</td>
              <td className="py-2.5 text-right pl-3">
                <span className={`text-[10px] uppercase tracking-[0.15em] ${itemPaid ? "text-[var(--eb-success)]" : "text-[var(--eb-text-faint)]"}`}>
                  {itemPaid ? "in escrow" : "unfunded"}
                </span>
              </td>
            </tr>
          )}
          <tr>
            <td className="pt-3 text-[var(--eb-text)] font-[800]">Total</td>
            <td className="pt-3 text-right text-2xl font-[800] text-[var(--eb-primary-deep)]">{fmt(total)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <div className="mt-4 flex items-center gap-2 text-xs text-[var(--eb-text-dim)]">
        {sfPaid && itemPaid ? <ShieldCheck className="w-4 h-4 text-[var(--eb-success)]" /> : <Lock className="w-4 h-4 text-[var(--eb-primary)]" />}
        <span>
          {sfPaid && itemPaid
            ? "All funds held in escrow — released to agent on your approval."
            : "Funds held in escrow until you approve the photos."}
        </span>
      </div>
    </div>
  );
}
